import { CollectDataStepType } from 'src/constants/collect-data/enums'
import type { InsuranceStepConfig } from 'src/interfaces/onboardingTemplate'
import {
  defaultOnboardingTemplateStepRowScalars,
  StepApprovalType,
} from 'src/interfaces/onboardingTemplate'
import {
  dedupeInsurancePolicyProductsForSync,
  mintInsuranceCoverageStepLabel,
} from 'src/utils/collectDataTemplateEditor/insuranceSync'
import { defaultStepConfigForType } from 'src/utils/collectDataTemplateEditor/stepApiMapping'
import {
  applyLabelToNewEditorStep,
  labelForNewEditorStep,
  stepTypeUsesNumberedLabelSuffix,
} from 'src/utils/collectDataTemplateEditor/stepLabels'
import { mintFlowEditorLocalId } from './ids'
import type { FlowEditorStepDraft, InsuranceCoverageDraftParams } from './types'

/**
 * Build `FlowEditorStepDraft`s for newly added steps of the given types, ready
 * to feed an `addSteps` document op. Mirrors the legacy `addStepsToConfig` step
 * creation: default config per type + unique numbered labels for form /
 * sign-contract. `order` is 0 — the `addSteps` op re-derives it from position.
 *
 * `existingSteps` supplies the labels already in use (pass the document's steps)
 * so numbered suffixes stay unique. Types are assumed already filtered for
 * feature-flag availability by the caller.
 */
export function createStepDrafts(
  types: CollectDataStepType[],
  configLocalId: string,
  existingSteps: readonly Pick<FlowEditorStepDraft, 'collectDataTemplateStepLabel'>[],
): FlowEditorStepDraft[] {
  const pendingLabels = new Set<string>()

  return types.map((type) => {
    const baseConfig = defaultStepConfigForType(type)
    const label = labelForNewEditorStep(type, existingSteps, pendingLabels)

    if (stepTypeUsesNumberedLabelSuffix(type)) {
      pendingLabels.add(label)
    }

    return {
      localId: mintFlowEditorLocalId('step'),
      serverId: undefined,
      configLocalId,
      type,
      order: 0,
      collectDataTemplateStepLabel: label,
      ...defaultOnboardingTemplateStepRowScalars(),
      config: applyLabelToNewEditorStep(type, baseConfig, label),
    }
  })
}

/**
 * Build the insurance-coverage `FlowEditorStepDraft`s for a policy's products,
 * ready to feed a `replaceGroupSteps` op (a synced insurance group contains only
 * these). Mirrors the legacy `syncInsuranceCoverageStepsWithPolicy` row shape:
 * one row per unique product, label `insurance_<productId>`, internal approval.
 */
export function insuranceCoverageDrafts(
  configLocalId: string,
  params: InsuranceCoverageDraftParams,
): FlowEditorStepDraft[] {
  const uniqueProducts = dedupeInsurancePolicyProductsForSync(params.products)

  return uniqueProducts.map((product) => {
    const label = mintInsuranceCoverageStepLabel(product.insuranceProductId)
    const base = defaultStepConfigForType(
      CollectDataStepType.InsuranceCoverage,
    ) as InsuranceStepConfig

    return {
      localId: mintFlowEditorLocalId('step'),
      serverId: undefined,
      configLocalId,
      type: CollectDataStepType.InsuranceCoverage,
      order: 0,
      collectDataTemplateStepLabel: label,
      ...defaultOnboardingTemplateStepRowScalars(),
      config: {
        ...base,
        approvalType: StepApprovalType.InternalApproval,
        delayed: params.groupDelayed,
        insurancePolicyId: params.policyRowId,
        insurancePolicyProductId: product.id,
        label,
        groupTitle: params.policyName,
      },
    }
  })
}
